import { useRef, useState, type DragEvent } from 'react';
import type {
  EngagementLetterParseResponse,
  EngagementLetterApplyResponse,
} from '@stfors/shared-types';
import { ApiError } from '../../api/client';
import { useParseEngagementLetter } from '../../api/queries/legal-document-ingest';
import { EngagementLetterReview } from './EngagementLetterReview';
import './engagement-ingest.css';

// Where the ingested letter attaches. Existing project → update that
// project + its client. New project → create a draft project + client.
export type IngestMode =
  | { kind: 'existing_project'; projectId: number }
  | { kind: 'new_project' };

const MAX_BYTES = 50 * 1024 * 1024;

interface Props {
  mode: IngestMode;
  onApplied: (res: EngagementLetterApplyResponse) => void;
  // Optional: when provided, a Cancel affordance is shown (used by the
  // toggled global-intake panel). Omit it for an always-visible dropzone
  // embedded in a section (the project Documents box).
  onCancel?: () => void;
}

// Drag-and-drop a signed engagement letter → parse → hand off to the
// editable review form.
export function EngagementLetterIngest({ mode, onApplied, onCancel }: Props): JSX.Element {
  const parse = useParseEngagementLetter();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<EngagementLetterParseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (f: File): Promise<void> => {
    setError(null);
    const isPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setError('Please choose a PDF file.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('File must be under 50 MB.');
      return;
    }
    setFile(f);
    try {
      const res = await parse.mutateAsync(f);
      setParsed(res);
    } catch (err) {
      setFile(null);
      setError(
        err instanceof ApiError && err.status === 400
          ? 'Could not read this PDF — is it a text PDF (not a scan)?'
          : err instanceof ApiError && err.status === 403
            ? "You don't have permission to ingest documents."
            : 'Could not parse the document. Try again.',
      );
    }
  };

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  const reset = (): void => {
    setParsed(null);
    setFile(null);
  };

  if (parsed && file) {
    return (
      <EngagementLetterReview
        mode={mode}
        file={file}
        extracted={parsed.extracted}
        suggestedClients={parsed.suggested_client_matches}
        // Reset back to the dropzone after a successful apply so the box is
        // ready for the next letter, then notify the parent.
        onApplied={(res) => {
          reset();
          onApplied(res);
        }}
        // When no parent Cancel is wired (embedded dropzone), Cancel just
        // returns to the empty dropzone.
        onCancel={onCancel ?? reset}
        onBack={reset}
      />
    );
  }

  return (
    <div className="eli-wrap">
      <div
        className={`eli-dropzone${dragOver ? ' eli-dropzone-over' : ''}${parse.isPending ? ' eli-dropzone-busy' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !parse.isPending && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !parse.isPending) inputRef.current?.click();
        }}
        role="button"
        tabIndex={0}
        aria-label="Drop a signed engagement letter PDF"
      >
        {parse.isPending ? (
          <p className="eli-drop-title">Reading the document…</p>
        ) : (
          <>
            <p className="eli-drop-title">Drop a signed engagement letter PDF here</p>
            <p className="eli-drop-meta">
              or click to choose a file — we’ll read it on-box and pre-fill the details for you to review.
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void handleFile(f);
          }}
        />
      </div>

      {error && <p className="lgd-error" role="alert">{error}</p>}

      {onCancel && (
        <div className="eli-actions">
          <button type="button" className="lgd-link" onClick={onCancel} disabled={parse.isPending}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
